import React, { useEffect, useState } from "react";
import { db, update } from "../firebase";
import { collection, doc, getDocs, increment, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import MovieForm from "../components/movieForm";
import LoginForm from "../components/loginForm";
import { MovieType } from "./movies";
import dayjs from "dayjs";
import { Button, Modal, Popconfirm, Table } from "antd";
import { Link } from "react-router-dom";
import { MailOutlined, DeleteOutlined, MailFilled, GiftOutlined, PlusSquareFilled, GiftFilled, SettingOutlined } from "@ant-design/icons";
import { stripeAmountToChfs } from "../utils";
import PotForm from "../components/potForm";
import SettingsForm, { SettingsType } from "../components/settingsForm";

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [movies, setMovies] = useState<MovieType[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [selectedMovie, setSelectedMovie] = useState<MovieType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [potTotal, setPotTotal] = useState<number>(0);
  const [showPot, setShowPot] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [settings, setSettings] = useState<SettingsType>({description: ""});

  useEffect(() => {
    const adminCheck = async () => {
      try {
        await getDocs(collection(db, "admin"));
        setIsAdmin(true);
      } catch (err) {
        setIsAdmin(false);
      }
    };
    adminCheck();
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    const docRef = doc(db, "pot", "global");
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      const data = snapshot?.data();
      setPotTotal(stripeAmountToChfs(data?.amount) || 0);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const docRef = doc(db, "settings", "public");
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      const data = snapshot?.data();
      setSettings({ description: data?.description || "" });
    });

    return () => unsubscribe();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const collectionRef = collection(db, "movies");
    const filteredQuery = query(collectionRef,   where('status', 'not-in', ['deleted', 'pending']));    
    const unsubscribe = onSnapshot(filteredQuery, (snapshot) => {
      const documents = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          details: data.details,
          status: data.status,
          amount: data.amount,
          goal: data.goal,
          date: data.date ? dayjs(data.date) : undefined,
          description: data.description,
          pseudo: data.pseudo,
          donation: data.donation ?? 0,
          image: data.image,
          emails: data.emails,
        };
      });

      const statusOrder: Record<string, number> = {
        requested: 1,
        validated: 2,
        canceled: 3,
        archived: 4,
      };
      
      const sortedMovies = documents.sort((a, b) => {
        const statusA = statusOrder[a.status] || 5; // Default to lowest priority for unknown statuses
        const statusB = statusOrder[b.status] || 5;
      
        // Sort by status order first
        if (statusA !== statusB) {
          return statusA - statusB;
        }
      
        // If statuses are the same, sort by name
        return a.name?.localeCompare(b.name);
      });

      setMovies(sortedMovies);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  const handleOpenModal = (movie: MovieType) => {
    setSelectedMovie(movie);
  };

  const handleCloseModal = () => {
    setSelectedMovie(null);
  };

  const handleOpenEmail = async (movie: MovieType, subject = "", body = "") => {
    setLoading(true);
    const recipients = Array.from(movie.emails || []).join(",");
    const mailtoLink = `mailto:?bcc=${recipients}&subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    setLoading(false);
    window.location.href = mailtoLink;
  };

  const handleOpenNewsletter = async () => {
    setLoading(true);
    const newsletter = await getDocs(collection(db, "newsletter"));
    const emails = new Set();

    newsletter.docs.forEach((doc) => {
      const data = doc.data();
      if (data.subscribed) {
        emails.add(doc.id);
      }
    });

    const recipients = Array.from(emails).join(",");
    const body = "";
    const mailtoLink = `mailto:?bcc=${recipients}&subject=${encodeURIComponent(
      "Newsletter Cinemir"
    )}&body=${encodeURIComponent(body)}`;

    setLoading(false);
    window.location.href = mailtoLink;
  };

  const deleteMovie = async (movieId: string) => {
    setLoading(true);
    await update("movies", movieId, { status: "deleted" });
    setLoading(false);
  };

  const collectDonation = async (movieId: string, amount?: number) => {
    if (!amount) return;
    await update("pot", "global", { amount: increment(amount) });
    await update("movies", movieId, { donation: 0 });
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (image: string, movie: MovieType) => (
        image && <img src={image} alt={movie.name} style={{ width: "50px" }} />
      ),
    },
    {
      title: "Film",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <b>{name}</b>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Validé", value: "validated" },
        { text: "Proposé", value: "requested" },
        { text: "Annulé", value: "canceled" },
        { text: "Archivé", value: "archived" },
      ],
      onFilter: (value: string | number | boolean | bigint, record: MovieType) =>
        record.status === value,
      render: (status: string) => {
        switch (status) {
          case "validated":
            return "Validé";
          case "requested":
            return "Proposé";
          case "canceled":
            return "Annulé";
          case "archived":
            return "Archivé";
          default:
            return "";
        }
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: dayjs.Dayjs | undefined) => date?.format("DD/MM/YYYY"),
    },
    {
      title: "Actuel",
      dataIndex: "amount",
      key: "amount",
      render: (amount?: number) => stripeAmountToChfs(amount) || 0,
    },
    {
      title: "Cible",
      dataIndex: "goal",
      key: "goal",
      render: (goal?: number) => stripeAmountToChfs(goal),
    },
    {
      title: "Donation",
      key: "donation",
      render: (movie: MovieType) => 
        movie.donation ? <Button
          loading={loading}
          icon={<GiftOutlined />}
          disabled={movie.status !== "canceled"}
          onClick={() =>
            collectDonation(movie.id, movie.donation)
          }
        >
          {stripeAmountToChfs(movie.donation)}
        </Button> : "",
    },
    {
      title: () => (
        <div className="flex gap-8 items-center">
          Actions
          <div className="flex gap-2">
            <Button
              icon={<PlusSquareFilled />}
              onClick={() => setSelectedMovie({} as MovieType)}
            >
              Ajouter un film
            </Button>
            <Button
              icon={<MailFilled />}
              onClick={handleOpenNewsletter}
            >
              Newsletter
            </Button>
            <Button
              icon={<GiftFilled />}
              onClick={() => setShowPot(true)}
            >
              Cagnotte: {potTotal}
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setShowSettings(true)}
            />
          </div>
        </div>
      ),

      key: "action",
      render: (_: any, movie: MovieType) => (
        <div className="flex gap-2">
          <Button type="primary" onClick={() => handleOpenModal(movie)}>
            Editer
          </Button>
          <Button danger>
            <Link
              target="_blank"
              to={`${process.env.REACT_APP_STRIPE_URL}/search?query=metadata%3AREIMBURSABLE%20metadata%3A${movie.id}%20is%3Apayment%20is%3Apaid`}
            >
              Remboursements
            </Link>
          </Button>
          <Button
            loading={loading}
            icon={<MailOutlined />}
            onClick={() =>
              handleOpenEmail(movie, `Information concernant le film: ${movie.name}`)
            }
          >
            Information
          </Button>
          <Button
            loading={loading}
            icon={<MailOutlined />}
            onClick={() =>
              handleOpenEmail(
                movie,
                `Annulation du film: ${movie.name}`,
                `Bonjour, nous vous informons que le film "${movie.name}" doit malheureusement être déprogrammé car le financement n'a pas été atteint.`
              )
            }
          >
            Annulation
          </Button>
          <Popconfirm
            title="Supprimer ce film?"
            onConfirm={() => deleteMovie(movie.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button danger loading={loading}><DeleteOutlined /></Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      {isAdmin ? (
        <>
          <Table
            dataSource={movies}
            columns={columns}
            rowKey="id"
            pagination={false}
          />

          <Modal
            title="Edition du film"
            open={!!selectedMovie}
            onCancel={handleCloseModal}
            footer={null}
            destroyOnHidden
            centered
          >
            {selectedMovie && <MovieForm movie={selectedMovie} close={handleCloseModal} />}
          </Modal>
          <Modal
            title="Cagnotte"
            open={showPot}
            onCancel={() => setShowPot(false)}
            footer={null}
            destroyOnHidden
          >
            <PotForm amount={potTotal} close={() => setShowPot(false)} />
          </Modal>
          <Modal
            title="Paramètres"
            open={showSettings}
            onCancel={() => setShowSettings(false)}
            footer={null}
            destroyOnHidden
          >
            <SettingsForm description={settings.description} close={() => setShowSettings(false)} />
          </Modal>
        </>
      ) : (
        <LoginForm />
      )}
    </div>
  );
};

export default Admin;
